import { useState } from 'react';
import { Link } from 'react-router-dom';

interface NavbarProps {
  isScrolled: boolean;
}

export default function Navbar({ isScrolled }: NavbarProps) {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      const offset = 80;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
      setIsMobileMenuOpen(false);
    }
  };

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
      isScrolled ? 'bg-black shadow-lg' : 'bg-black/80'
    }`}>
      <div className="w-full px-4 sm:px-6 lg:px-12">
        <div className="flex items-center justify-between h-16 sm:h-18 md:h-20">
          {/* Logo */}
          <div className="flex-shrink-0">
            <Link to="/">
              <img 
                src="https://static.readdy.ai/image/26c3401126eecf15eb2286ded28883ff/289e4af9c1f47a64ff4db8c7e8c98e4a.jpeg" 
                alt="Metamorfosi Preparazioni Logo" 
                className="h-10 sm:h-12 md:h-14 w-auto cursor-pointer"
              />
            </Link>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center justify-center flex-1">
            <div className="flex items-center bg-white/10 backdrop-blur-sm rounded-full px-2 py-1.5">
              <button onClick={() => scrollToSection('servizi')} className="text-white hover:text-white hover:bg-[#E31E24] px-5 py-2 rounded-full transition-all duration-300 text-sm font-semibold cursor-pointer whitespace-nowrap">
                Servizi
              </button>
              <button onClick={() => scrollToSection('chi-siamo')} className="text-white hover:text-white hover:bg-[#E31E24] px-5 py-2 rounded-full transition-all duration-300 text-sm font-semibold cursor-pointer whitespace-nowrap">
                Chi Siamo
              </button>
              <Link to="/zone-servite" className="text-white hover:text-white hover:bg-[#E31E24] px-5 py-2 rounded-full transition-all duration-300 text-sm font-semibold cursor-pointer whitespace-nowrap">
                Zona Operativa
              </Link>
              <button onClick={() => scrollToSection('contatti')} className="text-white hover:text-white hover:bg-[#E31E24] px-5 py-2 rounded-full transition-all duration-300 text-sm font-semibold cursor-pointer whitespace-nowrap">
                Contatti
              </button>
            </div>
          </div>

          {/* Contact Icons - Desktop */}
          <div className="hidden lg:flex items-center space-x-3">
            <a href="tel:+393518312394" className="w-10 h-10 rounded-full bg-white/10 hover:bg-[#E31E24] flex items-center justify-center transition-all duration-300 cursor-pointer">
              <i className="ri-phone-line text-white text-lg"></i>
            </a>
            <a href="https://wa.me/393518312394" target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full bg-white/10 hover:bg-[#25D366] flex items-center justify-center transition-all duration-300 cursor-pointer">
              <i className="ri-whatsapp-line text-white text-lg"></i>
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button 
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="lg:hidden w-10 h-10 flex items-center justify-center text-white text-2xl cursor-pointer"
          >
            <i className={`ri-${isMobileMenuOpen ? 'close' : 'menu'}-line`}></i>
          </button>
        </div>

        {/* Mobile Menu */}
        <div className={`lg:hidden overflow-hidden transition-all duration-300 ${
          isMobileMenuOpen ? 'max-h-[600px] opacity-100' : 'max-h-0 opacity-0'
        }`}>
          <div className="bg-black border-t border-white/10 py-4">
            <div className="flex flex-col space-y-1">
              <button onClick={() => scrollToSection('servizi')} className="text-white font-semibold text-sm hover:text-[#E31E24] hover:bg-white/5 transition-all duration-300 text-left px-4 py-3 cursor-pointer">
                Servizi
              </button>
              <Link to="/tagliandi" onClick={() => setIsMobileMenuOpen(false)} className="text-white font-semibold text-sm hover:text-[#E31E24] hover:bg-white/5 transition-all duration-300 text-left px-4 py-3 block cursor-pointer pl-8">
                • Tagliandi
              </Link>
              <Link to="/impianto-frenante" onClick={() => setIsMobileMenuOpen(false)} className="text-white font-semibold text-sm hover:text-[#E31E24] hover:bg-white/5 transition-all duration-300 text-left px-4 py-3 block cursor-pointer pl-8">
                • Impianto Frenante
              </Link>
              <Link to="/manutenzione" onClick={() => setIsMobileMenuOpen(false)} className="text-white font-semibold text-sm hover:text-[#E31E24] hover:bg-white/5 transition-all duration-300 text-left px-4 py-3 block cursor-pointer pl-8">
                • Manutenzione
              </Link>
              <Link to="/diagnosi" onClick={() => setIsMobileMenuOpen(false)} className="text-white font-semibold text-sm hover:text-[#E31E24] hover:bg-white/5 transition-all duration-300 text-left px-4 py-3 block cursor-pointer pl-8">
                • Diagnosi Elettronica
              </Link>
              <button onClick={() => scrollToSection('chi-siamo')} className="text-white font-semibold text-sm hover:text-[#E31E24] hover:bg-white/5 transition-all duration-300 text-left px-4 py-3 cursor-pointer">
                Chi Siamo
              </button>
              <Link to="/zone-servite" onClick={() => setIsMobileMenuOpen(false)} className="text-white font-semibold text-sm hover:text-[#E31E24] hover:bg-white/5 transition-all duration-300 text-left px-4 py-3 block cursor-pointer">
                Zona Operativa
              </Link>
              <button onClick={() => scrollToSection('contatti')} className="text-white font-semibold text-sm hover:text-[#E31E24] hover:bg-white/5 transition-all duration-300 text-left px-4 py-3 cursor-pointer">
                Contatti
              </button>
              <div className="flex items-center justify-center space-x-4 px-4 pt-4 pb-2">
                <a href="https://wa.me/393518312394" target="_blank" rel="noopener noreferrer" className="w-11 h-11 rounded-full bg-[#E31E24] flex items-center justify-center hover:bg-[#c41a1f] transition-all duration-300 cursor-pointer">
                  <i className="ri-whatsapp-line text-white text-xl"></i>
                </a>
                <a
                  href="mailto:metamorfosi.preparazioni@gmail.com"
                  className="flex items-center space-x-3 text-white hover:text-[#E31E24] transition-colors duration-300 cursor-pointer"
                >
                  <i className="ri-mail-line text-xl"></i>
                  <span className="text-sm">metamorfosi.preparazioni@gmail.com</span>
                </a>
                <a href="tel:+393518312394" className="w-11 h-11 rounded-full bg-[#E31E24] flex items-center justify-center hover:bg-[#c41a1f] transition-all duration-300 cursor-pointer">
                  <i className="ri-phone-line text-white text-xl"></i>
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </nav>
  );
}
